import { api } from '@agency/shared';
import { Button, Chip, DataTable, PageHeader, type DataTableColumn } from '@agency/ui';
import { ExternalLink, RefreshCw, Send } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ErrorCallout, FilterSelect, IdCell, LoadMore, Page, Stamp, TableCard, UserLink } from '../../app/widgets.js';
import { MAIL_STATE_TONE } from '../../lib/labels.js';
import { useMutation } from '../../lib/mutate.js';
import { useSession } from '../../lib/session.js';
import { usePaged } from '../../lib/useApi.js';

export function MailPage() {
  const session = useSession();
  const mutation = useMutation();
  const [kind, setKind] = useState<string | undefined>(undefined);
  const [state, setState] = useState<string | undefined>(undefined);
  const input = useMemo(() => ({ limit: 100, ...(kind ? { kind: kind as api.adminOps.MailDelivery['kind'] } : {}), ...(state ? { state: state as api.adminOps.MailDelivery['state'] } : {}) }), [kind, state]);
  const mail = usePaged('admin.ops.mail.list', { input }, { refreshMs: 30_000 });
  const canRetry = session.has('device.manage');
  const viewer = mail.items.find((m) => m.viewerUrl)?.viewerUrl ?? null;

  const columns = useMemo<DataTableColumn<api.adminOps.MailDelivery>[]>(
    () => [
      { id: 'id', header: 'Delivery', cell: ({ row }) => <IdCell value={row.original.id} />, size: 110 },
      { id: 'kind', header: 'Kind', cell: ({ row }) => <span className="font-mono text-xs-plus uppercase text-mute">{row.original.kind.replace(/_/g, ' ')}</span>, size: 160 },
      { id: 'recipient', header: 'Recipient', cell: ({ row }) => <span className="flex items-center gap-2"><span className="font-mono text-xs-plus">{row.original.recipientMasked}</span>{row.original.recipientUserId ? <UserLink userId={row.original.recipientUserId} /> : null}</span>, size: 260 },
      { id: 'state', header: 'Status', cell: ({ row }) => <Chip tone={MAIL_STATE_TONE[row.original.state]}>{row.original.state}</Chip>, size: 100 },
      { id: 'attempts', header: 'Attempts', cell: ({ row }) => <span className="block text-right font-mono tabular-nums">{row.original.attempts}</span>, size: 80 },
      { id: 'template', header: 'Template', cell: ({ row }) => <span className="font-mono text-2xs text-mute">{row.original.templateVersion}</span>, size: 150 },
      { id: 'created', header: 'Created', cell: ({ row }) => <Stamp value={row.original.createdAt} />, size: 150 },
      { id: 'sent', header: 'Sent', cell: ({ row }) => <Stamp value={row.original.sentAt} relative />, size: 100 },
      { id: 'error', header: 'Last error', cell: ({ row }) => <span className="truncate text-mute" title={row.original.lastError ?? ''}>{row.original.lastError ?? '--'}</span>, size: 240 },
      {
        id: 'actions',
        header: '',
        size: 170,
        cell: ({ row }) => (
          <span className="flex gap-1">
            {row.original.viewerUrl ? <Button size="sm" variant="ghost" trailingArrow={false} icon={ExternalLink} onClick={() => window.open(row.original.viewerUrl!, '_blank', 'noopener')}>View</Button> : null}
            {row.original.state === 'failed' || row.original.state === 'bounced' ? <Button size="sm" variant="tertiary" icon={RefreshCw} disabled={!canRetry} onClick={() => void mutation.run('admin.ops.mail.retry', { params: { deliveryId: row.original.id }, input: {} }, { title: 'Delivery retried', invalidate: ['admin.ops.mail', 'admin.ops.outbox', 'admin.overview'] })}>Retry</Button> : null}
          </span>
        ),
      },
    ],
    [canRetry, mutation],
  );

  return (
    <Page>
      <PageHeader
        eyebrow="System operations"
        eyebrowPath="Mail"
        title="System mail"
        subtitle={mail.loading ? 'Loading deliveries.' : `${mail.items.length} deliveries shown · ${mail.items.filter((m) => m.state === 'failed' || m.state === 'bounced').length} need attention · recipients masked · no customer findings in any message`}
        actions={
          <span className="flex gap-2">
            <Button variant="tertiary" icon={Send} disabled={!canRetry} onClick={() => void mutation.run('admin.ops.mail.test', { input: {} }, { title: (data) => `Test message queued for ${data.delivery.recipientMasked}`, invalidate: ['admin.ops.mail', 'admin.ops.outbox', 'admin.ops.health'] })}>
              Send test email
            </Button>
            {viewer ? <Button variant="primary" icon={ExternalLink} onClick={() => window.open(new URL(viewer).origin + '/sim/mail', '_blank', 'noopener')}>Open mail viewer</Button> : <Button variant="primary" icon={RefreshCw} onClick={() => void mail.reload()}>Refresh</Button>}
          </span>
        }
      />
      <ErrorCallout error={mail.error} onRetry={() => void mail.reload()} className="mt-6" />
      <div className="mt-6">
        <TableCard title="Deliveries" subtitle="Invitation · access change · device alert · update notice · publication notice · operational failure · test" footer={<LoadMore hasMore={mail.hasMore} loading={mail.loadingMore} onLoadMore={() => void mail.loadMore()} />}>
          <div className="border-b border-hair px-5 py-3">
            <div className="flex flex-wrap items-end gap-3">
              <FilterSelect label="Kind" value={kind} onChange={setKind} options={api.adminOps.MAIL_KINDS.map((k) => ({ value: k, label: k.replace(/_/g, ' ') }))} width={190} />
              <FilterSelect label="Status" value={state} onChange={setState} options={api.adminOps.MAIL_STATES.map((s) => ({ value: s, label: s }))} width={140} />
            </div>
          </div>
          <DataTable<api.adminOps.MailDelivery> aria-label="Mail deliveries" columns={columns} rows={mail.items} rowKey={(r) => r.id} height={Math.min(720, Math.max(240, mail.items.length * 34 + 40))} state={mail.loading ? 'loading' : mail.items.length === 0 ? 'empty' : 'complete'} emptyMessage="No mail deliveries match these filters." noun="deliveries" isCritical={(r) => r.state === 'bounced'} />
        </TableCard>
      </div>
    </Page>
  );
}
